import React, { useEffect } from "react";
import { Box, Typography } from "../../../design-system";
import UploadDownloadArea from "./UploadDownloadArea";
import HeroClipDemo from "./HeroClipDemo";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * Variant H — the "glitch" creative landing. Dark, edgy, pixelated/glitchy-but-tasteful,
 * channeling the reference mockup: a glitching cherry logo, a chromatic RGB-split poster
 * headline (cherry + teal, never matrix-green), Anton display type, and a faint scanline/
 * pixel/noise texture. Reuses the REAL upload box + demo. Honest copy only (the app cuts in
 * the cloud — no "local-first / nothing uploaded" claims).
 */

const CHERRY = "#ff2e63";
const TEAL = "#27e0d2";

const FONT_LINK_ID = "cc-glitch-fonts";

const GlitchLandingPage: React.FC<{ onTrySample?: () => void }> = ({ onTrySample }) => {
  useEffect(() => {
    if (!document.getElementById(FONT_LINK_ID)) {
      const l = document.createElement("link");
      l.id = FONT_LINK_ID;
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Anton&family=JetBrains+Mono:wght@400;500;700&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  const scrollToUpload = () => {
    try { document.getElementById("cc-glitch-upload")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* ignore */ }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: "calc(100vh - 64px)", overflow: "hidden", background: "linear-gradient(180deg,#0b0810 0%,#07050a 100%)" }}>
      {/* ── texture: scanlines + pixel dots + noise (subtle) ── */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", opacity: 0.6,
        backgroundImage:
          "radial-gradient(rgba(255,46,99,0.10) 1px, transparent 1.5px)," +
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 3px)",
        backgroundSize: "26px 26px, 100% 3px",
      }} />
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", opacity: 0.06, mixBlendMode: "overlay",
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }} />

      {/* ── content ── */}
      <Box sx={{ position: "relative", zIndex: 2, maxWidth: "1100px", mx: "auto", px: { xs: "20px", md: "26px" }, pt: { xs: "40px", md: "60px" }, pb: { xs: "40px", md: "64px" }, textAlign: "center" }}>
        {/* glitching cherry logo */}
        <Box sx={{
          width: { xs: 92, md: 120 }, height: { xs: 92, md: 120 }, mx: "auto", mb: { xs: "20px", md: "26px" },
          // Council: glitch should be a brief ~150ms hardware-hiccup every ~6s, not a loop.
          animation: "ccbob 6s ease-in-out infinite, ccrgb 6.5s steps(1) infinite", willChange: "transform,filter",
          "@keyframes ccbob": { "50%": { transform: "translateY(-9px)" } },
          "@keyframes ccrgb": {
            "0%,97%,100%": { filter: "none" },
            "97.6%": { filter: `drop-shadow(3px 0 ${CHERRY}) drop-shadow(-3px 0 ${TEAL})` },
            "98.4%": { filter: `drop-shadow(-4px 0 ${CHERRY}) drop-shadow(4px 1px ${TEAL})`, transform: "translate(2px,-9px)" },
            "99.2%": { filter: `drop-shadow(2px -1px ${CHERRY}) drop-shadow(-2px 1px ${TEAL})` },
          },
        }}>
          <img src={logoSite} alt="CherryClipz" style={{ width: "100%", height: "100%", display: "block" }} />
        </Box>

        {/* eyebrow */}
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", letterSpacing: "0.3em", textTransform: "uppercase", color: "#8a8391", mb: { xs: "16px", md: "20px" } }}>
          <Box component="span" sx={{ color: CHERRY }}>●</Box>&nbsp;&nbsp;the spreadsheet for video
        </Typography>

        {/* chromatic-split poster headline */}
        <Box component="h1" sx={{ m: 0, fontFamily: "'Anton', 'Arial Narrow', sans-serif", textTransform: "uppercase", lineHeight: 0.9, letterSpacing: "0.005em", fontSize: { xs: "52px", sm: "76px", md: "104px" } }}>
          <Box
            component="span"
            data-text="CHERRY-PICK"
            sx={{
              position: "relative", display: "block", color: "#f3ecf1",
              "&::before, &::after": { content: "attr(data-text)", position: "absolute", top: 0, left: 0, width: "100%", color: "#f3ecf1", overflow: "hidden", clipPath: "inset(0 0 100% 0)" },
              "&::before": { left: "2px", textShadow: `-2px 0 ${CHERRY}`, animation: "ccg1 4.6s infinite linear alternate" },
              "&::after": { left: "-2px", textShadow: `2px 0 ${TEAL}`, animation: "ccg2 3.3s infinite linear alternate" },
              "@keyframes ccg1": { "0%,86%,100%": { clipPath: "inset(0 0 100% 0)" }, "88%": { clipPath: "inset(12% 0 60% 0)" }, "92%": { clipPath: "inset(48% 0 30% 0)" }, "96%": { clipPath: "inset(70% 0 8% 0)" } },
              "@keyframes ccg2": { "0%,82%,100%": { clipPath: "inset(0 0 100% 0)" }, "84%": { clipPath: "inset(30% 0 50% 0)" }, "90%": { clipPath: "inset(8% 0 78% 0)" }, "94%": { clipPath: "inset(62% 0 18% 0)" } },
            }}
          >
            CHERRY-PICK
          </Box>
          <Box component="span" sx={{ display: "block", background: `linear-gradient(100deg, #ff5c8a, ${CHERRY} 45%, #c20e45)`, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            THE GOOD ONES.
          </Box>
        </Box>

        {/* honest subhead */}
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, color: "#c2c7d6", fontSize: { xs: "16px", md: "20px" }, lineHeight: 1.55, maxWidth: "620px", mx: "auto", mt: { xs: "22px", md: "28px" } }}>
          Every sentence becomes a clip. <Box component="span" sx={{ color: "#f3ecf1", fontWeight: 600 }}>You cherry-pick — or let AI.</Box>
        </Typography>

        {/* upload */}
        <Box id="cc-glitch-upload" sx={{ maxWidth: "560px", mx: "auto", mt: { xs: "28px", md: "36px" }, p: { xs: "16px", md: "22px" }, borderRadius: "18px", border: "1px solid rgba(255,46,99,0.28)", background: "radial-gradient(120% 100% at 50% 0%, rgba(255,46,99,0.10), transparent 60%), rgba(255,255,255,0.02)" }}>
          <UploadDownloadArea />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: "18px" }}>
          <Box component="button" onClick={onTrySample}
            sx={{ cursor: "pointer", border: "none", borderRadius: "999px", backgroundColor: CHERRY, color: "#fff", fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, fontSize: "14px", padding: "11px 22px", "&:hover": { boxShadow: "0 0 30px -4px rgba(255,46,99,0.6)" } }}>
            ▶ Try it on a sample reel
          </Box>
        </Box>
      </Box>

      {/* spreadsheet proof */}
      <Box sx={{ position: "relative", zIndex: 2, maxWidth: "1100px", mx: "auto", px: { xs: "20px", md: "26px" }, pb: { xs: "48px", md: "72px" } }}>
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", letterSpacing: "0.26em", color: CHERRY, textTransform: "uppercase", mb: "12px", textAlign: "center" }}>// your footage, as a spreadsheet</Typography>
        <Box sx={{ borderRadius: "16px", border: "1px solid rgba(255,255,255,0.08)", overflow: "hidden", p: { xs: "10px", md: "14px" }, background: "rgba(255,255,255,0.015)" }}>
          <HeroClipDemo />
        </Box>
      </Box>
    </Box>
  );
};

export default GlitchLandingPage;
